import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FacebookLogo, InstagramLogo, LinkedinLogo, YoutubeLogo, PaperPlaneTilt } from '@phosphor-icons/react';
import { LOGO_URL, OFFICES } from '@/lib/constants';
import { useCMS } from '@/lib/useCMS';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { subscribeNewsletter } from '@/lib/api';
import { toast } from 'sonner';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { companyInfo } = useCMS();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const hqOffice = OFFICES.find((o) => o.isHQ);

  return (
    <footer data-testid="main-footer" className="bg-[#0A1F3D] text-white relative grain-overlay">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="container-custom py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold font-['Playfair_Display'] text-white mb-2">
                Subscribe to Our Newsletter
              </h3>
              <p className="text-white/60">Get exclusive travel deals and updates delivered to your inbox.</p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full md:w-auto gap-0">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-none min-w-0 md:min-w-[280px] flex-1 focus:ring-[#D4AF37]"
                required
              />
              <Button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] rounded-none px-6"
              >
                {loading ? (
                  <span className="spinner" />
                ) : (
                  <PaperPlaneTilt size={20} weight="fill" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Column 1: Logo & Social */}
          <div>
            <Link to="/">
              <img src={LOGO_URL} alt={companyInfo.name} className="h-14 mb-6 brightness-0 invert" />
            </Link>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              {companyInfo.tagline}
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/osakagroup93"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="social-icon"
              >
                <FacebookLogo size={20} />
              </a>
              <a
                href="https://www.instagram.com/osaka_group_official/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="social-icon"
              >
                <InstagramLogo size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/osakagroup/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="social-icon"
              >
                <LinkedinLogo size={20} />
              </a>
              <a
                href="https://www.youtube.com/c/OsakaGroup"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-youtube"
                className="social-icon"
              >
                <YoutubeLogo size={20} />
              </a>
            </div>
            {/* IATA Badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-sm">
              <span className="text-[#D4AF37] font-bold text-xs tracking-wider">DUAL IATA ACCREDITED</span>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-lg">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Group Companies', href: '/companies' },
                { label: 'Careers', href: '/careers' },
                { label: 'News & Media', href: '/news' },
                { label: 'Awards', href: '/awards' },
                { label: 'Partner With Us', href: '/partner-with-us' },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-lg">Services</h4>
            <ul className="space-y-3">
              {[
                { label: 'Visa & Passport', href: '/services' },
                { label: 'Air Tickets', href: '/services' },
                { label: 'Forex Services', href: '/services' },
                { label: 'Tour Packages', href: '/services' },
                { label: 'Study Abroad', href: '/services' },
                { label: 'Corporate Travel', href: '/services' },
                { label: 'Osaka Connect', href: '/services' },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-white/60 hover:text-[#D4AF37] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-lg">Contact Us</h4>
            {hqOffice && (
              <div className="space-y-4 text-sm">
                <p className="text-white/60">{hqOffice.address}</p>
                <p>
                  <span className="text-white/40">Phone: </span>
                  <a href={`tel:${hqOffice.phone}`} className="text-white/80 hover:text-[#D4AF37]">
                    {hqOffice.phone}
                  </a>
                </p>
                <p>
                  <span className="text-white/40">WhatsApp: </span>
                  <a
                    href={`https://wa.me/${companyInfo.whatsapp.replace(/\s/g, '')}`}
                    className="text-white/80 hover:text-[#D4AF37]"
                  >
                    {companyInfo.whatsapp}
                  </a>
                </p>
                <p>
                  <span className="text-white/40">Email: </span>
                  <a href={`mailto:${hqOffice.email}`} className="text-white/80 hover:text-[#D4AF37]">
                    {hqOffice.email}
                  </a>
                </p>
                <p className="text-white/40 pt-2">{companyInfo.workingHours}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
            <p>© {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-[#D4AF37] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
